import React, { useEffect, useState, useCallback } from "react";
import {
	Table,
	Button,
	Row,
	Col,
	Card,
	Space,
	Form,
	Input,
	notification,
	Modal,
	Spin,
} from "antd";
import {
	SolutionOutlined,
	EyeOutlined,
	SearchOutlined,
	ClearOutlined,
} from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import { Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import supportDeskService from "../../services/supportDesk";

const { confirm } = Modal;

const RequestSupportForm = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});
	const [searchForm] = Form.useForm();
	const [formVisible, setFormVisible] = useState(false);
	const isMobile = useMediaQuery({ maxWidth: 767 });

	// Use useCallback to memoize the data fetching function
	const fetchSupportDeskData = useCallback(async (params) => {
		setLoading(true);
		try {
			// This service now correctly handles the params object
			const response = await supportDeskService.getsupportDesk(params);

			// Correctly map the Laravel response to the component's state
			setData(response.data); // The array of items is in response.data
			setPagination({
				current: response.current_page,
				pageSize: response.per_page,
				total: response.total,
			});
		} catch (error) {
			notification.error({
				message: "Error Fetching Data",
				description:
					"There was a problem retrieving the support desk requests.",
			});
		} finally {
			setLoading(false);
		}
	}, []); // Empty dependency array means this function is created only once

	// Handle search form submission
	const onSearch = (values) => {
		// Reset to page 1 for a new search and pass search values
		fetchSupportDeskData({
			page: 1,
			pageSize: pagination.pageSize,
			...values,
		});
	};

	// Handle resetting the search form
	const onResetSearch = () => {
		searchForm.resetFields();
		fetchSupportDeskData({
			page: 1,
			pageSize: pagination.pageSize,
		});
	};

	// Handle table changes (pagination, sorting)
	const handleTableChange = (newPagination, filters, sorter) => {
		const { order, field } = sorter;
		const sortOrder =
			order === "ascend" ? "asc" : order === "descend" ? "desc" : undefined;

		fetchSupportDeskData({
			page: newPagination.current,
			// pageSize: newPagination.pageSize,
			sortBy: field,
			sortOrder: sortOrder,
			...searchForm.getFieldsValue(), // Include current search values
		});
	};

	// Fetch initial data on component mount
	useEffect(() => {
		fetchSupportDeskData({
			page: pagination.current,
			// pageSize: pagination.pageSize,
		});
	}, [fetchSupportDeskData]); // Dependency array includes the memoized function

	const columns = [
		{
			title: "Sequence No",
			dataIndex: "sequence_no",
			key: "sequence_no",
			fixed: !isMobile ? "left" : false,
			sorter: true,
		},
		{
			title: "Request Title",
			dataIndex: "request_title",
			key: "request_title",
			sorter: true,
			render: (text) =>
				text && text.length > 40 ? `${text.slice(0, 40)}...` : text,
		},
		{
			title: "Relevant ID",
			dataIndex: "relevant_id",
			key: "relevant_id",
			sorter: true,
		},
		{
			title: "Priority",
			dataIndex: "priority",
			key: "priority",
			sorter: true,
		},
		{
			title: "Phone",
			dataIndex: "phone",
			key: "phone",
			sorter: true,
		},
		{
			title: "Actions",
			dataIndex: "Actions",
			key: "Actions",
			width: 80,
			fixed: !isMobile ? "right" : false,
			render: (text, record) => (
				<Space>
					<Link to={"details/" + record.id}>
						<Button type='primary' icon={<EyeOutlined />} size='small' />
					</Link>
				</Space>
			),
		},
	];

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]} style={{ marginBottom: 16 }}>
				<Col span={24}>
					<Header
						icon={<SolutionOutlined />}
						title='Request Support Form'
						right={
							<Link to='/request-support-form/add'>
								<Button className='btn-blue' type='primary'>
									Add Request
								</Button>
							</Link>
						}
					/>
				</Col>
			</Row>

			<Card>
				<div style={{ marginBottom: 16 }}>
					<Button onClick={() => setFormVisible(!formVisible)}>
						{formVisible ? "Hide Search" : "Show Search"}
					</Button>
				</div>
				{formVisible && (
					<Form form={searchForm} onFinish={onSearch} layout='vertical'>
						<Spin spinning={loading}>
							<Card className='pt-6' bodyStyle={{ paddingBottom: 0 }}>
								<Row gutter={16}>
									{[
										{ key: "sequence_no", placeholder: "Sequence No" },
										{ key: "request_title", placeholder: "Request Title" },
										{ key: "relevant_id", placeholder: "Relevant ID" },
										{ key: "priority", placeholder: "Priority" },
									].map(({ key, placeholder }) => (
										<Col key={key} lg={6} md={12} sm={24} xs={24}>
											<Form.Item name={key}>
												<Input placeholder={placeholder} />
											</Form.Item>
										</Col>
									))}
								</Row>
								<Row gutter={16} justify='end'>
									<Col>
										<Space>
											<Button onClick={onResetSearch} icon={<ClearOutlined />}>
												Reset
											</Button>
											<Button
												className='btn-blue'
												htmlType='submit'
												type='primary'
												icon={<SearchOutlined />}
											>
												Search
											</Button>
										</Space>
									</Col>
								</Row>
							</Card>
						</Spin>
					</Form>
				)}
			</Card>

			<Card style={{ marginTop: 16 }}>
				<Table
					columns={columns}
					dataSource={data}
					rowKey='id' // It's good practice to explicitly set the rowKey
					scroll={{ x: 1200 }}
					sticky={true}
					loading={loading}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: false,
						// showTotal: (total, range) =>
						// `${range[0]}-${range[1]} of ${total} items`,
					}}
					onChange={handleTableChange}
				/>
			</Card>
		</DefaultLayout>
	);
};

export default RequestSupportForm;
